import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { isOnIndustryStepAndCredentialVerifiedNewUser } from '../experiments/acq111/acq111Utils';
import { getDist008Message } from '../experiments/dist008Utils';
import { VARIANT_B } from '../experiments/experimentDefaults';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { getIsGoogleCredentialAuthentication } from '../google-authentication/useGoogleCredentialForAuthentication';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useIsWideStep } from '../hooks/useIsWideStep';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getFlowKey } from '../routing/getFlowKey';
import { Data } from '../signup-data/DATA';
import { useSignupData } from '../signup-data/useSignupData';
import { getIsOAuthExistingUser } from '../store/app/selectors';
import { Step } from '../views/Step';
import { getMessageComponent } from './getMessageComponent';
import { getMessageKey } from './getMessageKey';
import { MessageKey } from './MessageKey';
import { MessagingConfig } from './MessagingConfig';

const useMessageKey = () => {
  const {
    currentStep
  } = useCurrentStep();
  const {
    data: industry
  } = useSignupData(Data.Industry);
  const {
    isMobile
  } = useResponsiveness();
  const isGoogleCredentialAuthentication = useSelector(getIsGoogleCredentialAuthentication);
  const isExistingOAuthUser = useSelector(getIsOAuthExistingUser);
  const {
    isAssignedToVariant: isAssignedToVariantDist008
  } = useExperiment(ExperimentKeys.DIST008, TrackWhen.enrolled);
  const {
    isAssignedToVariant: isAssignedToVariantAcq123
  } = useExperiment(ExperimentKeys.ACQ123, TrackWhen.never);
  const {
    isExperimentActive: acq117IsExperimentActive,
    parameter: acq117Parameter
  } = useExperiment(ExperimentKeys.ACQ117, TrackWhen.never);

  if (isAssignedToVariantAcq123()) {
    const flowSpecificMessaging = MessagingConfig.Desktop.get(getFlowKey());
    const messageKey = flowSpecificMessaging.get(Step.LandingPage);
    return {
      messageKey
    };
  }

  if (isOnIndustryStepAndCredentialVerifiedNewUser(currentStep, isGoogleCredentialAuthentication, isExistingOAuthUser)) {
    return {
      messageKey: MessageKey.GetStartedWithSalesHub
    };
  }

  if (isAssignedToVariantDist008()) {
    const dist008MessageKey = getDist008Message(currentStep);

    if (dist008MessageKey) {
      return {
        messageKey: dist008MessageKey
      };
    }
  }

  if (acq117IsExperimentActive && acq117Parameter === VARIANT_B && currentStep === Step.LandingPage) {
    return {
      messageKey: MessageKey.ACQ117
    };
  }

  return {
    messageKey: getMessageKey({
      currentStep,
      industry,
      isMobile
    })
  };
};

export const useMessage = () => {
  const {
    isWideStep
  } = useIsWideStep();
  const {
    shouldShowMessaging
  } = useSignupEmbedder();
  const {
    messageKey
  } = useMessageKey();
  const {
    isActive: isInstanceOfSignupEmbedder
  } = useSignupEmbedder();
  const MessageComponent = useMemo(() => {
    if (messageKey) {
      return getMessageComponent({
        messageKey
      });
    }

    return null;
  }, // Do not re-render when just the industry is changing and not the step: that's when the user is typing.
  // We can change this once we make it a dropdown.
  [messageKey]);
  const shouldDisplayMessaging = (isInstanceOfSignupEmbedder && !shouldShowMessaging ? false : !isWideStep) && MessageComponent;
  return {
    component: MessageComponent,
    shouldDisplayMessaging
  };
};